import React, { useEffect, useMemo, useState } from 'react';
import AdminShell from '../components/admin/AdminShell';
import LoadingDNA from '../components/LoadingDNA';
import { StatusBadge } from '../components/admin/AdminPrimitives';
import {
  formatCurrencyINRCompact,
  formatCurrencyINRFull,
  formatPercent,
} from '../utils/adminFormat';
import { getCompanyEmployees, getTripBudgetRevisions, getTrips, isApiError, TripRecord } from '../services/dashboardService';
import { getExpenseEvents, isExpenseApiError } from '../services/expenseService';

type KpiCard = {
  id: string;
  label: string;
  value: string;
  changeText: string;
  helperText: string;
  tone: 'neutral' | 'positive' | 'warning' | 'danger';
};

type TrendPoint = {
  key: string;
  label: string;
  spend: number;
  baseline: number;
  highlight?: boolean;
};

type DashboardExpense = {
  id: string;
  trip_id: string;
  employee_id: string;
  employeeName: string;
  department: string;
  managerName: string;
  category: string;
  amount: number;
  parsedDate: Date | null;
  status: string;
};

type ExceptionRow = {
  id: string;
  date: string;
  employee: string;
  category: string;
  amount: number;
  statusLabel: string;
  statusTone: 'warning' | 'danger' | 'neutral';
  owner: string;
  ageDays: number;
};

type CategoryAnalysisRow = {
  name: string;
  totalSpent: number;
  spendShare: number;
  employees: number;
  trips: number;
  expenseCount: number;
};

type EmployeeRow = {
  name: string;
  department: string;
  managerName: string;
  expenseCount: number;
  totalSpent: number;
  rejectedPercent: number;
};

type ViolationSnapshot = {
  id: string;
  title: string;
  meta: string;
  amount: number;
};

type TripBudgetHealthRow = {
  tripId: string;
  destination: string;
  status: string;
  budget: number;
  actual: number;
  budgetUsedPct: number;
  burnRatePerDay: number;
  projectedTotal: number;
  projectedDelta: number;
  willExceed: boolean;
};

type DepartmentBudgetHealthRow = {
  department: string;
  budget: number;
  actual: number;
  budgetUsedPct: number;
  burnRatePerDay: number;
  projectedTotal: number;
  projectedDelta: number;
  willExceed: boolean;
};

const monthKey = (date: Date): string => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
const monthLabel = (date: Date): string => date.toLocaleDateString('en-US', { month: 'short' });
const startOfWeek = (date: Date): Date => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};
const weekKey = (date: Date): string => startOfWeek(date).toISOString().slice(0, 10);
const weekLabel = (date: Date): string => startOfWeek(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const MS_PER_DAY = 1000 * 60 * 60 * 24;

const safeDate = (value?: string): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed;
};

const kpiToneStyles: Record<KpiCard['tone'], string> = {
  neutral: 'border-slate-200 bg-white',
  positive: 'border-emerald-200 bg-emerald-50/40',
  warning: 'border-amber-200 bg-amber-50/40',
  danger: 'border-red-200 bg-red-50/40',
};

const statusTone = (value: ExceptionRow['statusTone']): 'neutral' | 'warning' | 'danger' => value;

const rollingAverage = (values: number[], index: number, span = 3): number => {
  const start = Math.max(0, index - (span - 1));
  const slice = values.slice(start, index + 1);
  if (slice.length === 0) return 0;
  return slice.reduce((sum, item) => sum + item, 0) / slice.length;
};

const AdminDashboardPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [primaryKpis, setPrimaryKpis] = useState<KpiCard[]>([]);
  const [secondaryKpis, setSecondaryKpis] = useState<KpiCard[]>([]);
  const [activeKpiPage, setActiveKpiPage] = useState(0);
  const [monthlyTrend, setMonthlyTrend] = useState<TrendPoint[]>([]);
  const [weeklyTrend, setWeeklyTrend] = useState<TrendPoint[]>([]);
  const [trendMode, setTrendMode] = useState<'Weekly' | 'Monthly'>('Monthly');
  const [selectedTrendKey, setSelectedTrendKey] = useState<string | null>(null);
  const [allExpenses, setAllExpenses] = useState<DashboardExpense[]>([]);
  const [violationSnapshots, setViolationSnapshots] = useState<ViolationSnapshot[]>([]);
  const [tripBudgetRows, setTripBudgetRows] = useState<TripBudgetHealthRow[]>([]);
  const [departmentBudgetRows, setDepartmentBudgetRows] = useState<DepartmentBudgetHealthRow[]>([]);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        setErrorMessage(null);

        const [events, revisions, employees, trips] = await Promise.all([
          getExpenseEvents({ perPage: 500 }),
          getTripBudgetRevisions({ perPage: 500 }),
          getCompanyEmployees(),
          getTrips({ perPage: 500 }),
        ]);

        const employeeById = new Map(employees.map((item) => [item.id, item]));
        const expenses = events.flatMap((event) =>
          (event.splits || []).map((split) => {
            const employee = employeeById.get(split.beneficiary_employee_id);
            const dateRaw = split.created_at || event.created_at || null;
            return {
              id: split.id,
              trip_id: event.trip_id,
              employee_id: split.beneficiary_employee_id,
              employeeName: employee?.name || employee?.email || 'Employee',
              department: employee?.department || 'No department',
              managerName: employee?.manager_name || 'Unassigned',
              category: event.category || 'Uncategorized',
              amount: Number(split.split_amount || 0),
              parsedDate: dateRaw ? new Date(dateRaw) : null,
              status: String(split.status || '').toLowerCase(),
            };
          })
        );
        setAllExpenses(expenses);

        const now = new Date();
        const currentMonth = monthKey(now);
        const previousMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const previousMonth = monthKey(previousMonthDate);

        const currentMonthRows = expenses.filter((item) => item.parsedDate && monthKey(item.parsedDate) === currentMonth);
        const previousMonthRows = expenses.filter((item) => item.parsedDate && monthKey(item.parsedDate) === previousMonth);

        const currentMonthSpend = currentMonthRows.reduce((sum, item) => sum + item.amount, 0);
        const previousMonthSpend = previousMonthRows.reduce((sum, item) => sum + item.amount, 0);
        const mtdDeltaPercent = previousMonthSpend > 0 ? ((currentMonthSpend - previousMonthSpend) / previousMonthSpend) * 100 : 0;

        const pendingApprovals = currentMonthRows.filter((item) => item.status === 'pending');
        const previousPendingApprovals = previousMonthRows.filter((item) => item.status === 'pending');
        const pendingApprovalsDelta = previousPendingApprovals.length > 0
          ? ((pendingApprovals.length - previousPendingApprovals.length) / previousPendingApprovals.length) * 100
          : 0;

        const rejectedCurrentMonth = currentMonthRows.filter((item) => item.status === 'rejected');
        const rejectedPreviousMonth = previousMonthRows.filter((item) => item.status === 'rejected');
        const violationRate = currentMonthRows.length > 0 ? (rejectedCurrentMonth.length / currentMonthRows.length) * 100 : 0;
        const previousViolationRate = previousMonthRows.length > 0 ? (rejectedPreviousMonth.length / previousMonthRows.length) * 100 : 0;

        const approvedUnpaidRows = expenses.filter((item) => item.status === 'approved' || item.status === 'auto_approved');
        const reimbursedRows = expenses.filter((item) => item.status === 'reimbursed');

        const approvedUnpaidAmount = approvedUnpaidRows.reduce((sum, item) => sum + item.amount, 0);
        const completionTotal = reimbursedRows.length + approvedUnpaidRows.length;
        const reimbursementCompletionRate = completionTotal > 0 ? (reimbursedRows.length / completionTotal) * 100 : 100;

        const avgClaimValue = currentMonthRows.length > 0 ? currentMonthSpend / currentMonthRows.length : 0;

        const tripActualById = new Map<string, number>();
        const tripElapsedDaysById = new Map<string, number>();
        const tripTotalDaysById = new Map<string, number>();
        const deptActualByTrip = new Map<string, Map<string, number>>();

        expenses.forEach((item) => {
          if (!item.trip_id) return;
          tripActualById.set(item.trip_id, Number(tripActualById.get(item.trip_id) || 0) + Number(item.amount || 0));
          const deptMap = deptActualByTrip.get(item.trip_id) || new Map<string, number>();
          deptMap.set(item.department, Number(deptMap.get(item.department) || 0) + Number(item.amount || 0));
          deptActualByTrip.set(item.trip_id, deptMap);
        });

        const normalizedTrips = (trips || []).map((trip) => {
          const start = safeDate(trip.start_date);
          const end = safeDate(trip.end_date || trip.start_date);
          const budget = Math.max(0, Number(trip.estimated_budget || 0));
          const actual = Math.max(0, Number(tripActualById.get(trip.id) || 0));

          let totalDays = 1;
          let elapsedDays = actual > 0 ? 1 : 0;
          if (start && end) {
            const tripDays = Math.max(1, Math.ceil((end.getTime() - start.getTime()) / MS_PER_DAY) + 1);
            totalDays = tripDays;
            const nowMs = now.getTime();
            if (nowMs < start.getTime()) elapsedDays = 0;
            else if (nowMs >= end.getTime()) elapsedDays = totalDays;
            else elapsedDays = Math.max(1, Math.ceil((nowMs - start.getTime()) / MS_PER_DAY) + 1);
          }

          tripElapsedDaysById.set(trip.id, elapsedDays);
          tripTotalDaysById.set(trip.id, totalDays);

          const burnRatePerDay = elapsedDays > 0 ? actual / elapsedDays : 0;
          const projectedTotal = burnRatePerDay * totalDays;
          const projectedDelta = projectedTotal - budget;
          const budgetUsedPct = budget > 0 ? (actual / budget) * 100 : 0;
          const willExceed = budget > 0 && projectedTotal > budget;

          return {
            tripId: trip.id,
            destination: trip.destination || 'Unknown destination',
            status: String(trip.status || '').toLowerCase(),
            budget,
            actual,
            budgetUsedPct,
            burnRatePerDay,
            projectedTotal,
            projectedDelta,
            willExceed,
          } as TripBudgetHealthRow;
        });

        const totalTripBudget = normalizedTrips.reduce((sum, item) => sum + item.budget, 0);
        const totalTripActual = normalizedTrips.reduce((sum, item) => sum + item.actual, 0);
        const totalElapsedDays = normalizedTrips.reduce((sum, item) => sum + Number(tripElapsedDaysById.get(item.tripId) || 0), 0);
        const totalTripDays = normalizedTrips.reduce((sum, item) => sum + Number(tripTotalDaysById.get(item.tripId) || 0), 0);
        const overallBurnRate = totalElapsedDays > 0 ? totalTripActual / totalElapsedDays : 0;
        const overallProjectedTotal = totalTripDays > 0 ? overallBurnRate * totalTripDays : 0;
        const overallBudgetUsedPct = totalTripBudget > 0 ? (totalTripActual / totalTripBudget) * 100 : 0;
        const willExceedTrips = normalizedTrips.filter((item) => item.willExceed).length;

        const deptBudgetAccumulator = new Map<string, { budget: number; actual: number; elapsedDays: number; totalDays: number }>();
        normalizedTrips.forEach((tripRow) => {
          const deptMap = deptActualByTrip.get(tripRow.tripId) || new Map<string, number>();
          const tripActual = Math.max(0, Number(tripRow.actual || 0));
          const elapsedDays = Number(tripElapsedDaysById.get(tripRow.tripId) || 0);
          const totalDays = Number(tripTotalDaysById.get(tripRow.tripId) || 1);

          deptMap.forEach((deptActual, department) => {
            const share = tripActual > 0 ? deptActual / tripActual : 0;
            const allocatedBudget = tripRow.budget > 0 ? tripRow.budget * share : 0;
            const existing = deptBudgetAccumulator.get(department) || { budget: 0, actual: 0, elapsedDays: 0, totalDays: 0 };
            existing.actual += Number(deptActual || 0);
            existing.budget += allocatedBudget;
            existing.elapsedDays += elapsedDays * share;
            existing.totalDays += totalDays * share;
            deptBudgetAccumulator.set(department, existing);
          });
        });

        const departmentRows: DepartmentBudgetHealthRow[] = Array.from(deptBudgetAccumulator.entries())
          .map(([department, value]) => {
            const burnRatePerDay = value.elapsedDays > 0 ? value.actual / value.elapsedDays : 0;
            const projectedTotal = burnRatePerDay * Math.max(1, value.totalDays);
            const projectedDelta = projectedTotal - value.budget;
            const budgetUsedPct = value.budget > 0 ? (value.actual / value.budget) * 100 : 0;
            const willExceed = value.budget > 0 && projectedTotal > value.budget;
            return {
              department,
              budget: value.budget,
              actual: value.actual,
              budgetUsedPct,
              burnRatePerDay,
              projectedTotal,
              projectedDelta,
              willExceed,
            };
          })
          .sort((a, b) => b.projectedDelta - a.projectedDelta);

        setTripBudgetRows(normalizedTrips.sort((a, b) => {
          if (a.willExceed !== b.willExceed) return a.willExceed ? -1 : 1;
          return b.projectedDelta - a.projectedDelta;
        }).slice(0, 8));
        setDepartmentBudgetRows(departmentRows.slice(0, 8));

        const revisedTrips = new Set<string>();
        let totalRevisionDelta = 0;
        revisions.forEach((revision) => {
          revisedTrips.add(revision.trip_id);
          totalRevisionDelta += Number(revision.new_budget || 0) - Number(revision.old_budget || 0);
        });

        setPrimaryKpis([
          {
            id: 'total-spend-mtd',
            label: 'Total Spend (MTD)',
            value: formatCurrencyINRFull(currentMonthSpend),
            changeText: `${formatPercent(mtdDeltaPercent)} vs previous month`,
            helperText: `Previous month: ${formatCurrencyINRFull(previousMonthSpend)}`,
            tone: mtdDeltaPercent > 0 ? 'warning' : 'neutral',
          },
          {
            id: 'approved-unpaid',
            label: 'Approved, Unpaid Reimbursements',
            value: formatCurrencyINRFull(approvedUnpaidAmount),
            changeText: `${approvedUnpaidRows.length} claims pending payout`,
            helperText: 'Finance action required to close payout queue.',
            tone: approvedUnpaidAmount > 0 ? 'warning' : 'positive',
          },
          {
            id: 'violation-rate',
            label: 'Policy Violation Rate (MTD)',
            value: `${violationRate.toFixed(1)}%`,
            changeText: `${formatPercent(violationRate - previousViolationRate)} vs previous month`,
            helperText: `${rejectedCurrentMonth.length} rejected claims this month`,
            tone: violationRate > 5 ? 'danger' : violationRate > 2 ? 'warning' : 'positive',
          },
        ]);

        setSecondaryKpis([
          {
            id: 'claims-awaiting',
            label: 'Claims Awaiting Approval',
            value: String(pendingApprovals.length),
            changeText: `${formatPercent(pendingApprovalsDelta)} vs previous month`,
            helperText: 'Owned by manager and finance approvers.',
            tone: pendingApprovals.length > 0 ? 'warning' : 'positive',
          },
          {
            id: 'avg-claim-value',
            label: 'Average Claim Value (MTD)',
            value: formatCurrencyINRFull(avgClaimValue),
            changeText: `${currentMonthRows.length} submitted claims`,
            helperText: 'Current period average per split expense.',
            tone: 'neutral',
          },
          {
            id: 'budget-change',
            label: 'Net Budget Change (Cycle)',
            value: `${totalRevisionDelta >= 0 ? '+' : ''}${formatCurrencyINRFull(totalRevisionDelta)}`,
            changeText: `${revisedTrips.size} trips revised`,
            helperText: 'Sum of all approved budget revisions in cycle.',
            tone: totalRevisionDelta > 0 ? 'warning' : 'neutral',
          },
          {
            id: 'reimbursement-rate',
            label: 'Reimbursement Completion Rate',
            value: `${reimbursementCompletionRate.toFixed(1)}%`,
            changeText: `${reimbursedRows.length} reimbursed claims`,
            helperText: `${approvedUnpaidRows.length} approved claims still unpaid`,
            tone: reimbursementCompletionRate >= 90 ? 'positive' : reimbursementCompletionRate >= 75 ? 'warning' : 'danger',
          },
          {
            id: 'budget-used-cycle',
            label: 'Budget Used (Trip Cycle)',
            value: `${overallBudgetUsedPct.toFixed(1)}%`,
            changeText: `${formatCurrencyINRFull(totalTripActual)} actual vs ${formatCurrencyINRFull(totalTripBudget)} budget`,
            helperText: 'Budget consumption across visible trips.',
            tone: overallBudgetUsedPct >= 90 ? 'danger' : overallBudgetUsedPct >= 75 ? 'warning' : 'positive',
          },
          {
            id: 'burn-rate',
            label: 'Burn Rate (Daily)',
            value: `${formatCurrencyINRCompact(overallBurnRate)}/day`,
            changeText: `Projected total: ${formatCurrencyINRFull(overallProjectedTotal)}`,
            helperText: 'Actual spend pace based on elapsed trip days.',
            tone: overallProjectedTotal > totalTripBudget && totalTripBudget > 0 ? 'warning' : 'neutral',
          },
          {
            id: 'budget-overrun-risk',
            label: 'Will Exceed Budget Alerts',
            value: String(willExceedTrips),
            changeText: `${normalizedTrips.length} tracked trips`,
            helperText: 'Trips projected to exceed approved budget.',
            tone: willExceedTrips > 0 ? 'danger' : 'positive',
          },
        ]);

        const months: Date[] = [];
        for (let i = 6; i >= 0; i -= 1) {
          months.push(new Date(now.getFullYear(), now.getMonth() - i, 1));
        }

        const monthlyTotals = months.map((date) => {
          const key = monthKey(date);
          const spend = expenses
            .filter((item) => item.parsedDate && monthKey(item.parsedDate) === key)
            .reduce((sum, item) => sum + item.amount, 0);
          return { key, label: monthLabel(date), spend };
        });

        setMonthlyTrend(
          monthlyTotals.map((item, index) => ({
            key: item.key,
            label: item.label,
            spend: item.spend,
            baseline: rollingAverage(monthlyTotals.map((entry) => entry.spend), index),
            highlight: item.key === currentMonth,
          }))
        );

        const currentWeek = weekKey(now);
        const weeks: Date[] = [];
        for (let i = 6; i >= 0; i -= 1) {
          const date = new Date(now);
          date.setDate(now.getDate() - (i * 7));
          weeks.push(startOfWeek(date));
        }

        const weeklyTotals = weeks.map((date) => {
          const key = weekKey(date);
          const spend = expenses
            .filter((item) => item.parsedDate && weekKey(item.parsedDate) === key)
            .reduce((sum, item) => sum + item.amount, 0);
          return { key, label: weekLabel(date), spend };
        });

        setWeeklyTrend(
          weeklyTotals.map((item, index) => ({
            key: item.key,
            label: item.label,
            spend: item.spend,
            baseline: rollingAverage(weeklyTotals.map((entry) => entry.spend), index),
            highlight: item.key === currentWeek,
          }))
        );

        setSelectedTrendKey(currentMonth);

        const topViolations = expenses
          .filter((item) => item.status === 'rejected')
          .sort((a, b) => b.amount - a.amount)
          .slice(0, 4)
          .map((item) => ({
            id: item.id,
            title: `${item.category} claim rejected`,
            meta: `${item.employeeName} • ${item.department} • Owner: ${item.managerName}`,
            amount: item.amount,
          }));

        setViolationSnapshots(
          topViolations.length > 0
            ? topViolations
            : [{
                id: 'no-violations',
                title: 'No policy violations in current dataset',
                meta: 'All teams • Compliance checks passed',
                amount: 0,
              }]
        );

        setLastRefreshedAt(new Date());
      } catch (error) {
        if (isExpenseApiError(error)) {
          setErrorMessage(error.message);
        } else if (isApiError(error)) {
          setErrorMessage(error.message);
        } else if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('Failed to load admin dashboard.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, []);

  const activeTrend = trendMode === 'Monthly' ? monthlyTrend : weeklyTrend;

  const kpiPages = useMemo(() => {
    const allKpis = [...primaryKpis, ...secondaryKpis];
    const pageLabels = ['Executive Snapshot', 'Workflow Pulse', 'Budget Guardrails'];
    const pages: Array<{ id: string; label: string; cards: KpiCard[] }> = [];

    for (let i = 0; i < allKpis.length; i += 4) {
      const pageIndex = Math.floor(i / 4);
      pages.push({
        id: `kpi-page-${pageIndex + 1}`,
        label: pageLabels[pageIndex] || `Dashboard ${pageIndex + 1}`,
        cards: allKpis.slice(i, i + 4),
      });
    }

    return pages;
  }, [primaryKpis, secondaryKpis]);

  useEffect(() => {
    if (kpiPages.length === 0) return;
    if (activeKpiPage < kpiPages.length) return;
    setActiveKpiPage(0);
  }, [activeKpiPage, kpiPages]);

  useEffect(() => {
    if (activeTrend.length === 0) return;
    const exists = selectedTrendKey && activeTrend.some((item) => item.key === selectedTrendKey);
    if (exists) return;
    const highlighted = activeTrend.find((item) => item.highlight) || activeTrend[activeTrend.length - 1];
    setSelectedTrendKey(highlighted?.key || null);
  }, [activeTrend, selectedTrendKey]);

  const selectedTrendPoint = useMemo(() => {
    if (activeTrend.length === 0) return null;
    if (!selectedTrendKey) return activeTrend[activeTrend.length - 1];
    return activeTrend.find((item) => item.key === selectedTrendKey) || activeTrend[activeTrend.length - 1];
  }, [activeTrend, selectedTrendKey]);

  const periodExpenses = useMemo(() => {
    if (!selectedTrendPoint) return allExpenses;
    return allExpenses.filter((item) => {
      if (!item.parsedDate) return false;
      if (trendMode === 'Monthly') return monthKey(item.parsedDate) === selectedTrendPoint.key;
      return weekKey(item.parsedDate) === selectedTrendPoint.key;
    });
  }, [allExpenses, selectedTrendPoint, trendMode]);

  const exceptionRows = useMemo<ExceptionRow[]>(() => {
    const actionable = periodExpenses.filter((item) => ['pending', 'approved', 'auto_approved', 'rejected'].includes(item.status));
    return actionable
      .map((item) => {
        const now = new Date();
        const ageDays = item.parsedDate ? Math.max(0, Math.floor((now.getTime() - item.parsedDate.getTime()) / (1000 * 60 * 60 * 24))) : 0;

        let statusLabel = 'Needs review';
        let statusTone: ExceptionRow['statusTone'] = 'neutral';
        let owner = 'Finance Ops';

        if (item.status === 'pending') {
          statusLabel = 'Awaiting Approval';
          statusTone = 'warning';
          owner = 'Manager + Finance';
        } else if (item.status === 'approved' || item.status === 'auto_approved') {
          statusLabel = 'Approved, Unpaid';
          statusTone = 'warning';
          owner = 'Payroll / Finance';
        } else if (item.status === 'rejected') {
          statusLabel = 'Rejected';
          statusTone = 'danger';
          owner = 'Employee Resubmission';
        }

        return {
          id: item.id,
          date: item.parsedDate ? item.parsedDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-',
          employee: item.employeeName,
          category: item.category,
          amount: item.amount,
          statusLabel,
          statusTone,
          owner,
          ageDays,
        };
      })
      .sort((a, b) => {
        if (a.statusTone === 'danger' && b.statusTone !== 'danger') return -1;
        if (a.statusTone !== 'danger' && b.statusTone === 'danger') return 1;
        if (b.ageDays !== a.ageDays) return b.ageDays - a.ageDays;
        return b.amount - a.amount;
      })
      .slice(0, 10);
  }, [periodExpenses]);

  const categoryRows = useMemo<CategoryAnalysisRow[]>(() => {
    const grouped = new Map<string, { spend: number; employees: Set<string>; trips: Set<string>; count: number }>();

    periodExpenses.forEach((item) => {
      const existing = grouped.get(item.category) || { spend: 0, employees: new Set<string>(), trips: new Set<string>(), count: 0 };
      existing.spend += item.amount;
      existing.count += 1;
      existing.employees.add(item.employee_id);
      if (item.trip_id) existing.trips.add(item.trip_id);
      grouped.set(item.category, existing);
    });

    const totalSpend = Array.from(grouped.values()).reduce((sum, item) => sum + item.spend, 0);
    return Array.from(grouped.entries())
      .map(([name, value]) => ({
        name,
        totalSpent: value.spend,
        spendShare: totalSpend > 0 ? (value.spend / totalSpend) * 100 : 0,
        employees: value.employees.size,
        trips: value.trips.size,
        expenseCount: value.count,
      }))
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, 8);
  }, [periodExpenses]);

  const employeeRows = useMemo<EmployeeRow[]>(() => {
    const grouped = new Map<string, { name: string; department: string; managerName: string; count: number; rejected: number; total: number }>();

    periodExpenses.forEach((item) => {
      const existing = grouped.get(item.employee_id) || {
        name: item.employeeName,
        department: item.department,
        managerName: item.managerName,
        count: 0,
        rejected: 0,
        total: 0,
      };
      existing.count += 1;
      existing.total += item.amount;
      if (item.status === 'rejected') existing.rejected += 1;
      grouped.set(item.employee_id, existing);
    });

    return Array.from(grouped.values())
      .map((item) => ({
        name: item.name,
        department: item.department,
        managerName: item.managerName,
        expenseCount: item.count,
        totalSpent: item.total,
        rejectedPercent: item.count > 0 ? (item.rejected / item.count) * 100 : 0,
      }))
      .sort((a, b) => b.totalSpent - a.totalSpent)
      .slice(0, 8);
  }, [periodExpenses]);

  const maxTrendSpend = useMemo(() => {
    const values = activeTrend.flatMap((item) => [item.spend, item.baseline]);
    return Math.max(...values, 1);
  }, [activeTrend]);

  return (
    <AdminShell
      pageTitle="Enterprise Expense Command Center"
      subtitle="Operational visibility for spend, compliance risk, and reimbursement workflow."
    >
      {isLoading ? <LoadingDNA /> : null}
      {errorMessage ? <p className="rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">{errorMessage}</p> : null}

      {!isLoading ? (
        <div className="space-y-5">
          <section>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="text-lg font-bold text-slate-900">KPI Dashboards</h2>
                <p className="text-sm text-slate-600">Each dashboard is limited to 4 cards for cleaner scanning.</p>
              </div>
              <div className="rounded-full bg-slate-100 p-1 text-sm font-semibold">
                {kpiPages.map((page, index) => (
                  <button
                    key={page.id}
                    type="button"
                    onClick={() => setActiveKpiPage(index)}
                    className={`rounded-full px-3 py-1 ${activeKpiPage === index ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
                  >
                    {page.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {(kpiPages[activeKpiPage]?.cards || []).map((kpi) => (
                <article key={kpi.id} className={`rounded-xl border px-5 py-4 ${kpiToneStyles[kpi.tone]}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">{kpi.label}</p>
                  <p className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900">{kpi.value}</p>
                  <p className="mt-2 text-sm font-semibold text-slate-700">{kpi.changeText}</p>
                  <p className="mt-1 text-sm text-slate-600">{kpi.helperText}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="grid gap-4 xl:grid-cols-[2fr_1fr]">
            <article className="rounded-xl border border-slate-200 bg-white px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Spend Trend vs 3-Period Baseline</h2>
                  <p className="text-sm text-slate-600">Bar: actual spend. Amber marker: rolling baseline for early variance detection.</p>
                </div>
                <div className="rounded-full bg-slate-100 p-1 text-sm font-semibold">
                  <button
                    type="button"
                    onClick={() => setTrendMode('Weekly')}
                    className={`rounded-full px-3 py-1 ${trendMode === 'Weekly' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
                  >
                    Weekly
                  </button>
                  <button
                    type="button"
                    onClick={() => setTrendMode('Monthly')}
                    className={`rounded-full px-3 py-1 ${trendMode === 'Monthly' ? 'bg-slate-900 text-white' : 'text-slate-600'}`}
                  >
                    Monthly
                  </button>
                </div>
              </div>

              {activeTrend.some((point) => point.spend > 0) ? (
                <>
                  <div className="mt-4 grid grid-cols-[64px_1fr] gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
                    <div className="flex h-56 flex-col justify-between text-xs font-semibold text-slate-500">
                      <span>{formatCurrencyINRCompact(maxTrendSpend)}</span>
                      <span>{formatCurrencyINRCompact(maxTrendSpend / 2)}</span>
                      <span>₹0</span>
                    </div>
                    <div className="flex h-56 items-end justify-between gap-2">
                      {activeTrend.map((point) => {
                        const spendHeight = point.spend > 0 ? Math.max(6, Math.round((point.spend / maxTrendSpend) * 100)) : 0;
                        const baselineHeight = point.baseline > 0 ? Math.round((point.baseline / maxTrendSpend) * 100) : 0;
                        const isSelected = selectedTrendPoint?.key === point.key;

                        return (
                          <button
                            key={point.key}
                            type="button"
                            onClick={() => setSelectedTrendKey(point.key)}
                            className="flex w-full flex-col items-center"
                            title={`${point.label}: spend ${formatCurrencyINRFull(point.spend)}, baseline ${formatCurrencyINRFull(point.baseline)}`}
                          >
                            <div className="mb-1 text-[11px] font-semibold text-slate-600">{formatCurrencyINRCompact(point.spend)}</div>
                            <div className="relative flex h-40 w-full items-end rounded-t-md bg-slate-200/60">
                              <div
                                className={`w-full rounded-t-md ${isSelected ? 'bg-slate-900' : 'bg-slate-400'}`}
                                style={{ height: `${spendHeight}%` }}
                              />
                              <span className="absolute left-1 right-1 border-t-2 border-amber-500" style={{ bottom: `${baselineHeight}%` }} />
                            </div>
                            <div className={`mt-2 text-xs font-semibold ${isSelected ? 'text-slate-900' : 'text-slate-500'}`}>{point.label}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="mt-3 grid gap-3 rounded-xl border border-slate-200 bg-white p-3 md:grid-cols-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Selected period</p>
                      <p className="text-sm font-bold text-slate-900">{selectedTrendPoint?.label || '-'}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Actual spend</p>
                      <p className="text-sm font-bold text-slate-900">{formatCurrencyINRFull(selectedTrendPoint?.spend || 0)}</p>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Variance vs baseline</p>
                      <p className={`text-sm font-bold ${(selectedTrendPoint?.spend || 0) - (selectedTrendPoint?.baseline || 0) > 0 ? 'text-red-700' : 'text-emerald-700'}`}>
                        {formatCurrencyINRFull((selectedTrendPoint?.spend || 0) - (selectedTrendPoint?.baseline || 0))}
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <div className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-8 text-sm font-semibold text-slate-600">
                  No expense activity found for the selected reporting range. Expand the range or remove filters.
                </div>
              )}
            </article>

            <article className="rounded-xl border border-slate-200 bg-white px-5 py-4">
              <h2 className="text-lg font-bold text-slate-900">Exception Snapshot</h2>
              <p className="text-sm text-slate-600">Highest value rejected claims to triage first.</p>
              <ul className="mt-3 space-y-3">
                {violationSnapshots.map((item) => (
                  <li key={item.id} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <p className="text-sm font-bold text-slate-900">{item.title}</p>
                    <p className="text-xs text-slate-600">{item.meta}</p>
                <p className={`mt-1 text-sm font-bold ${item.amount > 0 ? 'text-red-700' : 'text-emerald-700'}`}>{formatCurrencyINRFull(item.amount)}</p>
                  </li>
                ))}
              </ul>
              <button type="button" className="mt-4 inline-flex h-10 w-full items-center justify-center rounded-lg bg-slate-900 text-sm font-semibold text-white hover:bg-slate-800">
                Open Policy Report
              </button>
            </article>
          </section>

          <section className="grid gap-4 xl:grid-cols-2">
            <article className="rounded-xl border border-slate-200 bg-white px-5 py-4">
              <div className="flex items-center justify-between gap-2">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Trip Budget vs Actual</h2>
                  <p className="text-sm text-slate-600">% used, burn rate, and projected overrun alerts by trip.</p>
                </div>
              </div>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-2">Trip</th>
                      <th className="pb-2 text-right">Budget</th>
                      <th className="pb-2 text-right">Actual</th>
                      <th className="pb-2 text-right">% Used</th>
                      <th className="pb-2 text-right">Burn/Day</th>
                      <th className="pb-2 text-right">Projected</th>
                      <th className="pb-2">Alert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tripBudgetRows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-6 text-sm font-semibold text-slate-600">No trip budget data available.</td>
                      </tr>
                    ) : (
                      tripBudgetRows.map((row) => (
                        <tr key={row.tripId} className="border-b border-slate-100 text-sm text-slate-700">
                          <td className="py-3">
                            <p className="font-semibold text-slate-900">{row.destination}</p>
                            <p className="text-xs text-slate-500">#{row.tripId.slice(0, 8)} • {row.status}</p>
                          </td>
                          <td className="py-3 text-right font-semibold text-slate-900">{formatCurrencyINRFull(row.budget)}</td>
                          <td className="py-3 text-right">{formatCurrencyINRFull(row.actual)}</td>
                          <td className="py-3 text-right">{row.budgetUsedPct.toFixed(1)}%</td>
                          <td className="py-3 text-right">{formatCurrencyINRCompact(row.burnRatePerDay)}/d</td>
                          <td className="py-3 text-right font-semibold">{formatCurrencyINRFull(row.projectedTotal)}</td>
                          <td className="py-3">
                            {row.willExceed ? (
                              <StatusBadge
                                label={`Will exceed by ${formatCurrencyINRCompact(Math.max(0, row.projectedDelta))}`}
                                tone="danger"
                              />
                            ) : (
                              <StatusBadge label="On track" tone="positive" />
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="rounded-xl border border-slate-200 bg-white px-5 py-4">
              <h2 className="text-lg font-bold text-slate-900">Department Budget vs Actual</h2>
              <p className="text-sm text-slate-600">Department-level burn view derived from trip spend distribution.</p>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[860px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-2">Department</th>
                      <th className="pb-2 text-right">Allocated Budget</th>
                      <th className="pb-2 text-right">Actual</th>
                      <th className="pb-2 text-right">% Used</th>
                      <th className="pb-2 text-right">Burn/Day</th>
                      <th className="pb-2 text-right">Projected</th>
                      <th className="pb-2">Alert</th>
                    </tr>
                  </thead>
                  <tbody>
                    {departmentBudgetRows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-6 text-sm font-semibold text-slate-600">No department budget signal available yet.</td>
                      </tr>
                    ) : (
                      departmentBudgetRows.map((row) => (
                        <tr key={row.department} className="border-b border-slate-100 text-sm text-slate-700">
                          <td className="py-3 font-semibold text-slate-900">{row.department}</td>
                          <td className="py-3 text-right font-semibold">{formatCurrencyINRFull(row.budget)}</td>
                          <td className="py-3 text-right">{formatCurrencyINRFull(row.actual)}</td>
                          <td className="py-3 text-right">{row.budgetUsedPct.toFixed(1)}%</td>
                          <td className="py-3 text-right">{formatCurrencyINRCompact(row.burnRatePerDay)}/d</td>
                          <td className="py-3 text-right font-semibold">{formatCurrencyINRFull(row.projectedTotal)}</td>
                          <td className="py-3">
                            {row.willExceed ? (
                              <StatusBadge
                                label={`Risk +${formatCurrencyINRCompact(Math.max(0, row.projectedDelta))}`}
                                tone="danger"
                              />
                            ) : (
                              <StatusBadge label="On track" tone="positive" />
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </article>
          </section>

          <section className="space-y-4">
            <article className="rounded-xl border border-slate-200 bg-white px-5 py-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Exceptions Requiring Action</h2>
                  <p className="text-sm text-slate-600">Sorted by severity, age, and amount in selected period.</p>
                </div>
                <button type="button" className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
                  Export Queue
                </button>
              </div>

              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[900px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-2">Date</th>
                      <th className="pb-2">Employee</th>
                      <th className="pb-2">Category</th>
                      <th className="pb-2 text-right">Amount</th>
                      <th className="pb-2">Status</th>
                      <th className="pb-2">Owner</th>
                      <th className="pb-2 text-right">Age (Days)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {exceptionRows.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="py-6 text-sm font-semibold text-slate-600">
                          No actionable claims in this selected period.
                        </td>
                      </tr>
                    ) : (
                      exceptionRows.map((row) => (
                        <tr key={row.id} className="border-b border-slate-100 text-sm text-slate-700">
                          <td className="py-3">{row.date}</td>
                          <td className="py-3 font-semibold text-slate-900">{row.employee}</td>
                          <td className="py-3">{row.category}</td>
                          <td className="py-3 text-right font-semibold text-slate-900">{formatCurrencyINRFull(row.amount)}</td>
                          <td className="py-3">
                            <StatusBadge label={row.statusLabel} tone={statusTone(row.statusTone)} />
                          </td>
                          <td className="py-3">{row.owner}</td>
                          <td className="py-3 text-right font-semibold">{row.ageDays}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="rounded-xl border border-slate-200 bg-white px-5 py-4">
              <h2 className="text-lg font-bold text-slate-900">Category Variance Analysis</h2>
              <p className="text-sm text-slate-600">Actual spend concentration in selected period.</p>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[860px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-2">Category</th>
                      <th className="pb-2 text-right">Actual Spend</th>
                      <th className="pb-2 text-right">Share of Spend</th>
                      <th className="pb-2 text-right">Employees</th>
                      <th className="pb-2 text-right">Trips</th>
                      <th className="pb-2 text-right">Claims</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-6 text-sm font-semibold text-slate-600">
                          No category-level spend found in this selected period.
                        </td>
                      </tr>
                    ) : (
                      categoryRows.map((row) => (
                        <tr key={row.name} className="border-b border-slate-100 text-sm text-slate-700">
                          <td className="py-3 font-semibold text-slate-900">{row.name}</td>
                          <td className="py-3 text-right font-semibold text-slate-900">{formatCurrencyINRFull(row.totalSpent)}</td>
                          <td className="py-3 text-right">{row.spendShare.toFixed(1)}%</td>
                          <td className="py-3 text-right">{row.employees}</td>
                          <td className="py-3 text-right">{row.trips}</td>
                          <td className="py-3 text-right">{row.expenseCount}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </article>

            <article className="rounded-xl border border-slate-200 bg-white px-5 py-4">
              <h2 className="text-lg font-bold text-slate-900">Top Claimants (Selected Period)</h2>
              <div className="mt-3 overflow-x-auto">
                <table className="w-full min-w-[860px]">
                  <thead>
                    <tr className="border-b border-slate-200 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <th className="pb-2">Employee</th>
                      <th className="pb-2">Department</th>
                      <th className="pb-2">Manager</th>
                      <th className="pb-2 text-right">Claims</th>
                      <th className="pb-2 text-right">Rejected %</th>
                      <th className="pb-2 text-right">Total Spend</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-6 text-sm font-semibold text-slate-600">No employee spend data for this period.</td>
                      </tr>
                    ) : (
                      employeeRows.map((row) => (
                        <tr key={`${row.name}-${row.department}-${row.managerName}`} className="border-b border-slate-100 text-sm text-slate-700">
                          <td className="py-3 font-semibold text-slate-900">{row.name}</td>
                          <td className="py-3">{row.department}</td>
                          <td className="py-3">{row.managerName}</td>
                          <td className="py-3 text-right">{row.expenseCount}</td>
                          <td className="py-3 text-right">{row.rejectedPercent.toFixed(1)}%</td>
                          <td className="py-3 text-right font-semibold text-slate-900">{formatCurrencyINRFull(row.totalSpent)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </article>
          </section>
        </div>
      ) : null}
    </AdminShell>
  );
};

export default AdminDashboardPage;
